/**
 * Hook para gerenciar monitoramento de tempo de resposta
 * Integra com o backend via API e socket para tempo real
 */
import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import toastUtils from '../utils/toastUtils';
import api from '../services/api';
import { SocketContext } from '../context/Socket/SocketContext';
import { AuthContext } from '../context/Auth/AuthContext';

export const useTimeMonitoring = () => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [settings, setSettings] = useState({
    timeMonitoringEnabled: false,
    responseTimeLimit: 30,
    timeoutAction: 'user',
    timeoutTargetUserId: null,
    timeoutTargetQueueId: null,
    timeMonitoringScope: 'company',
    timeMonitoringWarning: true,
    timeMonitoringWarningMinutes: 5,
    limitActiveTicketsEnabled: false,
    maxActiveTicketsPerUser: 5,
    allowMultipleTickets: true,
    inactivityTimeoutMinutes: 30,
    autoLogoutInactive: false
  });

  const [activeTimers, setActiveTimers] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Carrega configurações do backend
   */
  const loadSettings = useCallback(async () => {
    try {
      if (isMounted.current) setLoading(true);
      const { data } = await api.get('/time-monitoring/settings');
      if (isMounted.current) setSettings(data);
    } catch (err) {
      if (!isMounted.current) return;
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado às configurações de monitoramento de tempo - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nas configurações de monitoramento de tempo');
        // Não mostrar toast para erros 500
      } else {
        setError('Erro ao carregar configurações de monitoramento de tempo');
        console.error('Error loading time monitoring settings:', err);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Salva configurações no backend
   */
  const saveSettings = useCallback(async (newSettings) => {
    try {
      if (isMounted.current) setLoading(true);
      const { data } = await api.put('/time-monitoring/settings', newSettings);
      if (isMounted.current) {
        setSettings(data.settings);
        toastUtils.success('Configurações de monitoramento de tempo atualizadas com sucesso!', 'Monitor Ativo');
      }
      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao salvar configurações de monitoramento de tempo - recurso pode não estar disponível no plano atual');
        toastUtils.error('Funcionalidade não disponível no seu plano atual', 'Erro no Monitor');
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor ao salvar configurações de monitoramento de tempo');
        toastUtils.error('Erro interno do servidor', 'Erro no Monitor');
      } else {
        const errorMessage = err.response?.data?.error || 'Erro ao salvar configurações';
        setError(errorMessage);
        toastUtils.error(errorMessage, 'Erro no Monitor');
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Inicia timer de resposta para um ticket
   */
  const startTicketTimer = useCallback(async (ticketId) => {
    try {
      await api.post(`/time-monitoring/tickets/${ticketId}/start-timer`);
      
      // Atualizar estado local
      if (isMounted.current) {
        setActiveTimers(prev => ({
          ...prev,
          [ticketId]: {
            startTime: new Date(),
            limitMinutes: settings.responseTimeLimit,
            warningMinutes: settings.timeMonitoringWarningMinutes,
            isActive: true
          }
        }));
      }

      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao iniciar cronômetro do ticket - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor ao iniciar cronômetro do ticket');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error starting ticket timer:', err);
        toastUtils.error('Erro ao iniciar cronômetro do ticket', 'Erro no Monitor');
      }
      return false;
    }
  }, [settings.responseTimeLimit, settings.timeMonitoringWarningMinutes]);

  /**
   * Para timer de resposta de um ticket
   */
  const stopTicketTimer = useCallback(async (ticketId) => {
    try {
      await api.post(`/time-monitoring/tickets/${ticketId}/stop-timer`);
      
      // Remover do estado local
      if (isMounted.current) {
        setActiveTimers(prev => {
          const newTimers = { ...prev };
          delete newTimers[ticketId];
          return newTimers;
        });
      }

      return true;
    } catch (err) {
      if (!isMounted.current) return false;
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao parar cronômetro do ticket - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor ao parar cronômetro do ticket');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error stopping ticket timer:', err);
        toastUtils.error('Erro ao parar cronômetro do ticket', 'Erro no Monitor');
      }
      return false;
    }
  }, []);

  /**
   * Obtém estatísticas de tempo de resposta
   */
  const getResponseTimeStats = useCallback(async (period = 'day') => {
    try {
      const { data } = await api.get(`/time-monitoring/stats?period=${period}`);
      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado às estatísticas de tempo de resposta - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nas estatísticas de tempo de resposta');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error getting response time stats:', err);
      }
      return { stats: [] };
    }
  }, []);

  /**
   * Atualiza atividade do usuário
   */
  const updateUserActivity = useCallback(async (activity = 'active') => {
    try {
      await api.post('/time-monitoring/activity', { activity });
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao atualizar atividade do usuário - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor ao atualizar atividade do usuário');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error updating user activity:', err);
      }
    }
  }, []);

  /**
   * Verifica se usuário pode aceitar mais tickets
   */
  const canAcceptTicket = useCallback(async () => {
    try {
      if (!settings.limitActiveTicketsEnabled) {
        return { canAccept: true, reason: null };
      }

      const { data } = await api.get('/time-monitoring/user/can-accept-ticket');
      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado à verificação de limite de tickets - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor na verificação de limite de tickets');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error checking if user can accept ticket:', err);
      }
      return { canAccept: false, reason: 'Erro ao verificar limite de tickets' };
    }
  }, [settings.limitActiveTicketsEnabled]);

  /**
   * Obtém estatísticas do usuário (tickets ativos)
   */
  const getUserTicketStats = useCallback(async () => {
    try {
      const { data } = await api.get('/time-monitoring/user/stats');
      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado às estatísticas do usuário - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor nas estatísticas do usuário');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error getting user ticket stats:', err);
      }
      return { activeTicketsCount: 0, maxActiveTickets: 0 };
    }
  }, []);

  /**
   * Obtém status de atividade de todos os usuários (para dashboard)
   */
  const getAllUsersActivityStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/time-monitoring/users/activity-status');
      return data.users || [];
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao status de atividade dos usuários - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor no status de atividade dos usuários');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error getting all users activity status:', err);
      }
      return [];
    }
  }, []);

  /**
   * Obtém resumo de atividade dos usuários (para dashboard)
   */
  const getUsersActivitySummary = useCallback(async () => {
    try {
      const { data } = await api.get('/time-monitoring/users/activity-summary');
      return data;
    } catch (err) {
      // Verificar se é um erro de permissão (recurso não disponível)
      if (err?.response?.status === 403) {
        console.warn('Acesso negado ao resumo de atividade dos usuários - recurso pode não estar disponível no plano atual');
        // Não mostrar toast para erros 403
      } else if (err?.response?.status === 500) {
        console.warn('Erro interno do servidor no resumo de atividade dos usuários');
        // Não mostrar toast para erros 500
      } else {
        console.error('Error getting users activity summary:', err);
      }
      return {
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        offlineUsers: 0,
        averageActiveTickets: 0,
        totalActiveTickets: 0
      };
    }
  }, []);

  /**
   * Calcula tempo restante para um ticket
   */
  const getTimeRemaining = useCallback((ticketId) => {
    const timer = activeTimers[ticketId];
    if (!timer || !timer.isActive) return null;

    const now = new Date();
    const elapsed = (now.getTime() - timer.startTime.getTime()) / (1000 * 60); // em minutos
    const remaining = timer.limitMinutes - elapsed;

    return Math.floor(remaining); // Permite valores negativos para indicar overtime
  }, [activeTimers]);

  /**
   * Verifica se um ticket está próximo do timeout
   */
  const isNearTimeout = useCallback((ticketId) => {
    const timer = activeTimers[ticketId];
    if (!timer || !timer.isActive) return false;

    const remaining = getTimeRemaining(ticketId);
    return remaining !== null && remaining <= timer.warningMinutes;
  }, [activeTimers, getTimeRemaining]);

  /**
   * Verifica se um ticket atingiu o timeout
   */
  const isTimeout = useCallback((ticketId) => {
    const remaining = getTimeRemaining(ticketId);
    return remaining !== null && remaining <= 0;
  }, [getTimeRemaining]);

  // Configurar eventos de socket
  useEffect(() => {
    if (!socketManager) return;

    const socket = socketManager.getSocket();
    if (!socket) return;

    // Timer iniciado
    const handleTimerStarted = ({ ticketId, startTime, limitMinutes, warningMinutes }) => {
      if (!isMounted.current) return;
      setActiveTimers(prev => ({
        ...prev,
        [ticketId]: {
          startTime: new Date(startTime),
          limitMinutes,
          warningMinutes,
          isActive: true
        }
      }));
    };

    // Timer parado
    const handleTimerStopped = ({ ticketId }) => {
      if (!isMounted.current) return;
      setActiveTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[ticketId];
        return newTimers;
      });
    };

    // Aviso de timeout
    const handleTimerWarning = ({ ticketId, userId, timeRemaining, message }) => {
      if (!isMounted.current) return;
      if (userId === user.id) {
        toastUtils.warning(message, 'Tempo Registrado', {
          autoClose: 10000,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    };

    // Timeout atingido
    const handleTimerTimeout = ({ ticketId, originalUserId, action, success, details }) => {
      if (!isMounted.current) return;
      if (originalUserId === user.id) {
        const message = success 
          ? `Ticket #${ticketId} foi transferido por tempo limite`
          : `Falha ao transferir ticket #${ticketId} por tempo limite`;
        
        toastUtils.error(message, 'Erro no Monitor', {
          autoClose: 15000,
        });
      }

      // Remover timer local
      setActiveTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[ticketId];
        return newTimers;
      });
    };

    // Configurações atualizadas
    const handleSettingsUpdated = (newSettings) => {
      if (!isMounted.current) return;
      setSettings(prev => ({
        ...prev,
        ...newSettings
      }));
    };

    // Registrar eventos
    socket.on("ticket:timer:started", handleTimerStarted);
    socket.on("ticket:timer:stopped", handleTimerStopped);
    socket.on("ticket:timer:warning", handleTimerWarning);
    socket.on("ticket:timer:timeout", handleTimerTimeout);
    socket.on("time-monitoring:settings:updated", handleSettingsUpdated);

    // Cleanup
    return () => {
      socket.off("ticket:timer:started", handleTimerStarted);
      socket.off("ticket:timer:stopped", handleTimerStopped);
      socket.off("ticket:timer:warning", handleTimerWarning);
      socket.off("ticket:timer:timeout", handleTimerTimeout);
      socket.off("time-monitoring:settings:updated", handleSettingsUpdated);
    };
  }, [socketManager, user]);

  // Caregar configurações na inicialização
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Atualizar atividade do usuário periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      updateUserActivity();
    }, 60000); // A cada minuto

    return () => clearInterval(interval);
  }, [updateUserActivity]);

  return {
    settings,
    activeTimers,
    loading,
    error,
    loadSettings,
    saveSettings,
    startTicketTimer,
    stopTicketTimer,
    getResponseTimeStats,
    updateUserActivity,
    getTimeRemaining,
    isNearTimeout,
    isTimeout,
    canAcceptTicket,
    getUserTicketStats,
    getAllUsersActivityStatus,
    getUsersActivitySummary
  };
};